import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, List, Grid3x3, CalendarDays, LayoutGrid, Video, MapPin, Users, Clock } from "lucide-react";
import { toast } from "sonner";

const VIEWS = [
  { key: "month", label: "Month", icon: LayoutGrid },
  { key: "week", label: "Week", icon: CalendarDays },
  { key: "day", label: "Day", icon: CalendarIcon },
  { key: "list", label: "List", icon: List },
  { key: "grid", label: "Grid", icon: Grid3x3 },
];

const isoDay = (d) => d.toISOString().slice(0, 10);
const sameDay = (a, b) => isoDay(a) === isoDay(b);
const startOfWeek = (d) => { const x = new Date(d); const g = (x.getDay() + 6) % 7; x.setDate(x.getDate() - g); x.setHours(0,0,0,0); return x; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const fmtHM = (d) => `${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`;

export default function CalendarPage() {
  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragEvent, setDragEvent] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/interviews");
      setEvents((data || []).filter(e => e.scheduled_at));
    } catch { setEvents([]); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onDayDrop = async (day) => {
    if (!dragEvent) return;
    const oldDate = new Date(dragEvent.scheduled_at);
    const newDate = new Date(day);
    newDate.setHours(oldDate.getHours(), oldDate.getMinutes(), 0, 0);
    try {
      await api.patch(`/interviews/${dragEvent.interview_id}/reschedule`, { scheduled_at: newDate.toISOString() });
      toast.success(`Moved to ${newDate.toLocaleDateString()}`);
      load();
    } catch (e) { toast.error("Reschedule failed"); }
    setDragEvent(null); setDropTarget(null);
  };

  const shift = (dir) => {
    const c = new Date(cursor);
    if (view === "day") c.setDate(c.getDate() + dir);
    else if (view === "week") c.setDate(c.getDate() + dir * 7);
    else c.setMonth(c.getMonth() + dir); // month, list, grid all shift by month
    setCursor(c);
  };

  const label = useMemo(() => {
    if (view === "day") return cursor.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
    if (view === "week") { const s = startOfWeek(cursor); const e = addDays(s, 6); return `${s.toLocaleDateString(undefined, {month:"short", day:"numeric"})} – ${e.toLocaleDateString(undefined, {month:"short", day:"numeric", year:"numeric"})}`; }
    return cursor.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  }, [cursor, view]);

  return (
    <div className="space-y-4" data-testid="calendar-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold font-display flex items-center gap-2">
            <CalendarIcon className="w-6 h-6 text-pink" /> Calendar
          </h1>
          <p className="text-sm text-neutral-500 mt-1">All interviews, drag-and-drop to reschedule.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex items-center rounded-full bg-neutral-100 p-1" data-testid="calendar-view-switcher">
            {VIEWS.map(v => (
              <button
                key={v.key}
                onClick={() => setView(v.key)}
                data-testid={`calendar-view-${v.key}`}
                className={`text-xs font-medium px-3 py-1.5 rounded-full flex items-center gap-1.5 transition-colors ${view === v.key ? "bg-white shadow text-navy" : "text-neutral-600 hover:text-navy"}`}>
                <v.icon className="w-3.5 h-3.5" /> {v.label}
              </button>
            ))}
          </div>
          <Button size="sm" variant="outline" onClick={() => setCursor(new Date())} data-testid="calendar-today-btn">Today</Button>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Button size="sm" variant="ghost" onClick={() => shift(-1)} data-testid="calendar-prev"><ChevronLeft className="w-4 h-4" /></Button>
        <div className="text-lg font-semibold font-display flex-1 text-center" data-testid="calendar-label">{label}</div>
        <Button size="sm" variant="ghost" onClick={() => shift(1)} data-testid="calendar-next"><ChevronRight className="w-4 h-4" /></Button>
      </div>

      {loading ? (
        <div className="text-sm text-neutral-500 py-10 text-center">Loading interviews…</div>
      ) : view === "month" ? <MonthView cursor={cursor} events={events} setDragEvent={setDragEvent} dragEvent={dragEvent} onDrop={onDayDrop} dropTarget={dropTarget} setDropTarget={setDropTarget} />
        : view === "week" ? <WeekView cursor={cursor} events={events} setDragEvent={setDragEvent} onDrop={onDayDrop} setDropTarget={setDropTarget} dropTarget={dropTarget} />
        : view === "day" ? <DayView cursor={cursor} events={events} />
        : view === "list" ? <ListView events={events} cursor={cursor} />
        : <GridView events={events} cursor={cursor} setDragEvent={setDragEvent} onDrop={onDayDrop} dropTarget={dropTarget} setDropTarget={setDropTarget} />}
    </div>
  );
}

function EventPill({ ev, onDragStart, small }) {
  const dt = new Date(ev.scheduled_at);
  return (
    <div
      draggable
      onDragStart={(e) => { onDragStart?.(ev); try { e.dataTransfer.effectAllowed = "move"; } catch {} }}
      data-testid={`cal-event-${ev.interview_id}`}
      className={`bg-pink/10 hover:bg-pink/20 border-l-2 border-pink text-navy rounded px-1.5 py-0.5 text-[10px] truncate cursor-grab active:cursor-grabbing ${small ? "" : "mb-0.5"}`}
      title={`${ev.candidate_name} · ${fmtHM(dt)}`}>
      <span className="font-mono text-pink font-bold mr-1">{fmtHM(dt)}</span>
      {ev.candidate_name}
    </div>
  );
}

function DayCell({ day, cursor, events, onDrop, setDragEvent, dragEvent, dropTarget, setDropTarget }) {
  const key = isoDay(day);
  const inMonth = day.getMonth() === cursor.getMonth();
  const isToday = sameDay(day, new Date());
  const dayEvents = events.filter(e => sameDay(new Date(e.scheduled_at), day)).sort((a,b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
  return (
    <div
      onDragOver={(e) => { if (dragEvent) { e.preventDefault(); setDropTarget?.(key); } }}
      onDragLeave={() => setDropTarget?.(null)}
      onDrop={(e) => { e.preventDefault(); onDrop?.(day); }}
      data-testid={`cal-day-${key}`}
      className={`min-h-24 p-1.5 border-r border-b transition-colors ${!inMonth ? "bg-neutral-50/50 text-neutral-400" : "bg-white"} ${dropTarget === key ? "bg-pink/5 ring-2 ring-pink ring-inset" : ""}`}>
      <div className={`text-xs font-mono ${isToday ? "text-pink font-bold" : "text-neutral-500"}`}>{day.getDate()}</div>
      <div className="mt-1 space-y-0.5">
        {dayEvents.slice(0, 3).map(ev => <EventPill key={ev.interview_id} ev={ev} onDragStart={setDragEvent} />)}
        {dayEvents.length > 3 && <div className="text-[10px] text-neutral-400">+{dayEvents.length - 3} more</div>}
      </div>
    </div>
  );
}

function MonthView({ cursor, events, setDragEvent, dragEvent, onDrop, dropTarget, setDropTarget }) {
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const startGrid = startOfWeek(first);
  const days = Array.from({ length: 42 }, (_, i) => addDays(startGrid, i));
  return (
    <Card className="border-0 shadow-sm overflow-hidden" data-testid="calendar-month-view">
      <div className="grid grid-cols-7 bg-neutral-50 border-b text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">
        {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map(d => <div key={d} className="px-2 py-2">{d}</div>)}
      </div>
      <div className="grid grid-cols-7">
        {days.map(day => (
          <DayCell key={isoDay(day)} day={day} cursor={cursor} events={events} onDrop={onDrop} setDragEvent={setDragEvent} dragEvent={dragEvent} dropTarget={dropTarget} setDropTarget={setDropTarget} />
        ))}
      </div>
    </Card>
  );
}

function WeekView({ cursor, events, setDragEvent, onDrop, dropTarget, setDropTarget }) {
  const s = startOfWeek(cursor);
  const days = Array.from({ length: 7 }, (_, i) => addDays(s, i));
  return (
    <Card className="border-0 shadow-sm overflow-hidden" data-testid="calendar-week-view">
      <div className="grid grid-cols-7">
        {days.map(d => {
          const dEvents = events.filter(e => sameDay(new Date(e.scheduled_at), d)).sort((a,b)=>new Date(a.scheduled_at)-new Date(b.scheduled_at));
          const key = isoDay(d);
          const isToday = sameDay(d, new Date());
          return (
            <div key={key} className="border-r last:border-r-0 min-h-[400px]"
              onDragOver={(e) => { e.preventDefault(); setDropTarget?.(key); }}
              onDragLeave={() => setDropTarget?.(null)}
              onDrop={() => onDrop?.(d)}
              data-testid={`cal-week-day-${key}`}>
              <div className={`px-2 py-2 border-b text-center ${isToday ? "bg-pink/5" : "bg-neutral-50"}`}>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">{d.toLocaleDateString(undefined, { weekday: "short" })}</div>
                <div className={`text-lg font-bold ${isToday ? "text-pink" : ""}`}>{d.getDate()}</div>
              </div>
              <div className={`p-2 space-y-1 ${dropTarget === key ? "bg-pink/5" : ""}`}>
                {dEvents.map(ev => (
                  <div key={ev.interview_id}
                    draggable
                    onDragStart={() => setDragEvent(ev)}
                    className="bg-pink/10 border-l-2 border-pink rounded px-2 py-1.5 cursor-grab active:cursor-grabbing"
                    data-testid={`cal-week-event-${ev.interview_id}`}>
                    <div className="text-[10px] font-mono text-pink font-bold">{fmtHM(new Date(ev.scheduled_at))}</div>
                    <div className="text-xs font-semibold truncate">{ev.candidate_name}</div>
                    <div className="text-[10px] text-neutral-500 truncate">{ev.job_title}</div>
                  </div>
                ))}
                {dEvents.length === 0 && <div className="text-[10px] text-neutral-300 text-center py-4">—</div>}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function DayView({ cursor, events }) {
  const dayEvents = events.filter(e => sameDay(new Date(e.scheduled_at), cursor)).sort((a,b)=>new Date(a.scheduled_at)-new Date(b.scheduled_at));
  const hours = Array.from({ length: 12 }, (_, i) => i + 8); // 8am - 8pm
  return (
    <Card className="border-0 shadow-sm" data-testid="calendar-day-view">
      <CardContent className="p-6">
        {dayEvents.length === 0 && <div className="text-sm text-neutral-500 py-10 text-center">No interviews on this day.</div>}
        <div className="space-y-2">
          {hours.map(h => {
            const hourEvents = dayEvents.filter(ev => new Date(ev.scheduled_at).getHours() === h);
            return (
              <div key={h} className="flex gap-4 items-start" data-testid={`cal-day-hour-${h}`}>
                <div className="w-16 text-xs font-mono text-neutral-400 pt-2 shrink-0">{String(h).padStart(2,"0")}:00</div>
                <div className="flex-1 border-t pt-2 min-h-10">
                  {hourEvents.map(ev => (
                    <div key={ev.interview_id} className="bg-pink/10 border-l-4 border-pink rounded-lg px-3 py-2 mb-2" data-testid={`cal-day-event-${ev.interview_id}`}>
                      <div className="flex items-center gap-2 text-xs text-pink font-mono font-bold"><Clock className="w-3 h-3" /> {fmtHM(new Date(ev.scheduled_at))} · {ev.duration_minutes || 45} min</div>
                      <div className="font-semibold mt-1">{ev.candidate_name}</div>
                      <div className="text-xs text-neutral-600">{ev.job_title}</div>
                      <div className="text-[11px] text-neutral-500 mt-1 flex items-center gap-3 flex-wrap">
                        {ev.interview_type === "VIDEO" ? <span className="flex items-center gap-1"><Video className="w-3 h-3" /> Video</span> : <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {ev.location_or_link || "In person"}</span>}
                        {ev.interviewer_name && <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {ev.interviewer_name}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function ListView({ events, cursor }) {
  const sameMonth = events.filter(e => { const d = new Date(e.scheduled_at); return d.getMonth() === cursor.getMonth() && d.getFullYear() === cursor.getFullYear(); });
  const sorted = sameMonth.sort((a,b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
  // Group by day
  const groups = {};
  sorted.forEach(ev => { const k = isoDay(new Date(ev.scheduled_at)); (groups[k] ||= []).push(ev); });
  const keys = Object.keys(groups);
  return (
    <Card className="border-0 shadow-sm" data-testid="calendar-list-view">
      <CardContent className="p-6">
        {keys.length === 0 && <div className="text-sm text-neutral-500 py-8 text-center">No interviews in this month.</div>}
        <div className="space-y-6">
          {keys.map(k => (
            <div key={k}>
              <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-2">
                {new Date(k).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}
              </div>
              <div className="space-y-1">
                {groups[k].map(ev => (
                  <div key={ev.interview_id} className="flex items-center gap-4 py-2 px-3 rounded-lg hover:bg-neutral-50" data-testid={`cal-list-item-${ev.interview_id}`}>
                    <div className="text-sm font-mono font-bold text-pink w-16">{fmtHM(new Date(ev.scheduled_at))}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate">{ev.candidate_name}</div>
                      <div className="text-xs text-neutral-500 truncate">{ev.job_title} · {ev.interviewer_name || "—"}</div>
                    </div>
                    <Badge variant="outline" className="text-[9px]">{ev.interview_type}</Badge>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function GridView({ events, cursor, setDragEvent, onDrop, dropTarget, setDropTarget }) {
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const daysIn = new Date(cursor.getFullYear(), cursor.getMonth()+1, 0).getDate();
  const days = Array.from({ length: daysIn }, (_, i) => new Date(cursor.getFullYear(), cursor.getMonth(), i+1));
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="calendar-grid-view">
      {days.map(d => {
        const key = isoDay(d);
        const dayEvents = events.filter(e => sameDay(new Date(e.scheduled_at), d)).sort((a,b)=>new Date(a.scheduled_at)-new Date(b.scheduled_at));
        const isToday = sameDay(d, new Date());
        return (
          <Card key={key} className={`border-0 shadow-sm ${dropTarget===key?"ring-2 ring-pink":""}`}
            onDragOver={(e) => { e.preventDefault(); setDropTarget?.(key); }}
            onDragLeave={() => setDropTarget?.(null)}
            onDrop={() => onDrop?.(d)}
            data-testid={`cal-grid-day-${key}`}>
            <CardContent className="p-3">
              <div className="flex items-center justify-between mb-1.5">
                <div className={`text-lg font-bold ${isToday?"text-pink":""}`}>{d.getDate()}</div>
                <div className="text-[10px] uppercase text-neutral-400">{d.toLocaleDateString(undefined, { weekday: "short" })}</div>
              </div>
              <div className="space-y-1">
                {dayEvents.length === 0 && <div className="text-[10px] text-neutral-300">—</div>}
                {dayEvents.map(ev => (
                  <div key={ev.interview_id}
                    draggable
                    onDragStart={() => setDragEvent(ev)}
                    className="bg-pink/10 border-l-2 border-pink rounded p-1.5 cursor-grab active:cursor-grabbing"
                    data-testid={`cal-grid-event-${ev.interview_id}`}>
                    <div className="text-[9px] font-mono text-pink font-bold">{fmtHM(new Date(ev.scheduled_at))}</div>
                    <div className="text-[11px] font-semibold truncate">{ev.candidate_name}</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
